import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config';
import axios from 'axios';

const ApiKeyContext = createContext();

export const useApiKey = () => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};

export const ApiKeyProvider = ({ children }) => {
  const [apiKey, setApiKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchApiKey = useCallback(async () => {
    console.log('🔍 ApiKeyContext - fetchApiKey called');
    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = getApiUrl('/user/api-key');
      console.log('Fetching API key from URL:', apiUrl);

      // Try to fetch API key from backend for authenticated users
      const response = await axios.get(apiUrl, { withCredentials: true });
      
      console.log('🔍 ApiKeyContext - Successfully fetched API key from backend:', response.data.api_key);
      setApiKey(response.data.api_key);
      // Store API key in sessionStorage for persistence
      sessionStorage.setItem('apiKey', response.data.api_key);
      setError(null);
    } catch (err) {
      console.error('🔍 ApiKeyContext - Error fetching API key:', err);
      console.error('Error response data:', err.response?.data);
      if (err.response?.status === 404) {
        console.warn('🔍 ApiKeyContext - API key endpoint returned 404. Please check the backend configuration.');
      } else {
        console.error('🔍 ApiKeyContext - Unexpected error while fetching API key:', err);
      }
      setApiKey(null);
      setError('Failed to fetch API key. Please contact support.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearApiKey = useCallback(() => {
    console.log('🔍 ApiKeyContext - clearApiKey called');
    setApiKey(null);
    sessionStorage.removeItem('apiKey');
    setError(null);
  }, []);

  // Helper function to make API calls with the stored API key
  const apiFetch = useCallback((endpoint, options = {}) => {
    if (!apiKey) {
      return Promise.reject(new Error('No API key available'));
    }

    return fetch(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });
  }, [apiKey]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('🔍 ApiKeyContext - State changed:');
    console.log('   isLoading:', isLoading);
    console.log('   apiKey available:', !!apiKey);
    console.log('   error:', error);
  }, [isLoading, apiKey, error]);

  useEffect(() => {
    console.log('Initializing ApiKeyContext...');
    setIsLoading(true);

    const initializeApiKey = async () => {
      try {
        console.log('🔍 Attempting to fetch API key from sessionStorage...');
        const storedApiKey = sessionStorage.getItem('apiKey');

        if (storedApiKey) {
          console.log('🔍 API key found in sessionStorage:', storedApiKey);
          setApiKey(storedApiKey);
        } else {
          console.warn('🔍 No API key found in sessionStorage. Fetching from backend...');
          await fetchApiKey();
        }
      } catch (err) {
        console.error('🔍 Error during API key initialization:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApiKey();
  }, []);

  const value = {
    apiKey,
    isLoading,
    error,
    fetchApiKey,
    clearApiKey,
    apiFetch,
  };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};